import { useClient } from '@/composables/useClient'
import { HttpError } from '@/queries/auth-error'
import { mutationOptions } from '@/queries/mutation-options'
import type { contract } from '@repo/api-contracts'
import type { ServerInferRequest } from '@ts-rest/core'
import { noop } from 'lodash'
import { RefreshResponse, SessionResponse } from '@repo/api-types'

type LoginRequest = ServerInferRequest<typeof contract.auth.login>['body']
type ForgotPasswordRequest = ServerInferRequest<typeof contract.auth.forgotPassword>['body']
type ConfirmForgotPasswordRequest = ServerInferRequest<typeof contract.auth.confirmForgotPassword>['body']
type ResetPasswordRequest = ServerInferRequest<typeof contract.auth.resetPassword>['body']
type LoginResult = { accessToken: string }

export const authMutations = {
  login: () =>
    mutationOptions({
      mutationFn: async ({ email, password }: LoginRequest): Promise<LoginResult> => {
        const client = await useClient()
        const { status, body } = await client.auth.login({ body: { email, password } })

        if (status === 200) {
          return body
        }

        throw new HttpError(status, body, 'Failed to log in')
      },
      invalidation: 'skip',
      // Defining onError here overrides `defaultOptions.mutations.onError` in
      // queryClient.ts, suppressing the global toast. Login.vue renders its
      // own inline error (e.g. "invalid credentials"), which is more useful
      // than a transient toast for authentication failures.
      onError: (error) => {
        console.error(error)
      },
    }),

  forgotPassword: () =>
    mutationOptions({
      mutationFn: async ({ email }: ForgotPasswordRequest): Promise<void> => {
        const client = await useClient()
        const { status, body } = await client.auth.forgotPassword({ body: { email } })

        if (status !== 200) {
          throw new HttpError(status, body, 'Failed to send reset email')
        }
      },
      invalidation: 'skip',
      // Suppress global toast; screen renders inline callout copy.
      onError: noop,
    }),

  confirmForgotPassword: () =>
    mutationOptions({
      mutationFn: async ({ email, password, confirmationCode }: ConfirmForgotPasswordRequest): Promise<void> => {
        const client = await useClient()
        const { status, body } = await client.auth.confirmForgotPassword({
          body: { email, password, confirmationCode },
        })

        if (status !== 200) {
          throw new HttpError(status, body, "We couldn't change your password. Please try again.")
        }
      },
      invalidation: 'skip',
      // Suppress global toast; screen renders inline callout copy.
      onError: noop,
    }),

  resetPassword: () =>
    mutationOptions({
      mutationFn: async ({ password, previousPassword }: ResetPasswordRequest): Promise<void> => {
        const client = await useClient()
        const { status, body } = await client.auth.resetPassword({
          body: {
            previousPassword,
            password,
          },
        })

        if (status !== 200) {
          throw new HttpError(status, body, 'Failed to reset password')
        }
      },
      invalidation: 'skip',
    }),

  refresh: () =>
    mutationOptions({
      mutationFn: async (): Promise<RefreshResponse> => {
        const client = await useClient()
        const { status, body } = await client.auth.refresh()

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to refresh session')
      },
      invalidation: 'skip',
    }),

  session: () =>
    mutationOptions({
      mutationFn: async (): Promise<SessionResponse> => {
        const client = await useClient()
        const { status, body } = await client.auth.session()

        if (status === 200) return body

        throw new HttpError(status, body, 'No active session')
      },
      invalidation: 'skip',
    }),

  logout: () =>
    mutationOptions({
      mutationFn: async (): Promise<void> => {
        const client = await useClient()
        const { status, body } = await client.auth.logout({ body: {} })

        if (status !== 200) {
          throw new HttpError(status, body, 'Failed to log out')
        }
      },
      // no invalidation since we clear the vue-query cache on logout
      invalidation: 'skip',
    }),
}
